import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  CheckSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  RefreshCw,
  ListTodo,
} from 'lucide-react';
import { taskAPI, serviceRequestAPI } from '../../services/api';
import { format, formatDistanceToNow } from 'date-fns';

// ── Status display config ──────────────────────────────────────────────────
const STATUS_CONFIG = {
  PENDING:           { label: 'Pending',        className: 'bg-amber-100 text-amber-700',   Icon: Clock },
  ASSIGNED:          { label: 'Assigned',        className: 'bg-blue-100 text-blue-700',    Icon: Clock },
  IN_PROGRESS:       { label: 'In Progress',     className: 'bg-blue-100 text-blue-700',    Icon: RefreshCw },
  REVIEWING:         { label: 'Under Review',    className: 'bg-purple-100 text-purple-700', Icon: Clock },
  REVISION_REQUIRED: { label: 'Needs Revision',  className: 'bg-red-100 text-red-700',      Icon: AlertCircle },
  COMPLETED:         { label: 'Completed',       className: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle },
  CANCELLED:         { label: 'Cancelled',       className: 'bg-gray-100 text-gray-500',    Icon: AlertCircle },
};

const SERVICE_TYPE_LABELS = {
  PROFILE_ASSESSMENT:       'Profile Assessment',
  UNIVERSITY_SHORTLISTING:  'Preapplication Support',
  APPLICATION_ASSISTANCE:   'Apply University',
  VISA_GUIDANCE:            'Visa & Interview Support',
  SCHOLARSHIP_SEARCH:       'Ticket & Travel Support',
  LOAN_ASSISTANCE:          'Education Loan',
  ACCOMMODATION_HELP:       'Find Accommodation',
  PRE_DEPARTURE_ORIENTATION:'Find Jobs Abroad',
};

const FILTERS = [
  { key: 'ALL',       label: 'All Tasks' },
  { key: 'PENDING',   label: 'Pending' },
  { key: 'COMPLETED', label: 'Completed' },
];

// ── Main component ────────────────────────────────────────────────────────
export default function MyTasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks]                   = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [refreshing, setRefreshing]         = useState(false);
  const [filter, setFilter]                 = useState('ALL');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, reqRes] = await Promise.all([
        taskAPI.getAll().catch(() => ({ tasks: [] })),
        serviceRequestAPI.getAll().catch(() => ({ serviceRequests: [] })),
      ]);
      setTasks(tasksRes.tasks || []);
      setServiceRequests(reqRes.serviceRequests || []);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // Build service-request lookup map
  const srLookup = {};
  serviceRequests.forEach(sr => { srLookup[sr.serviceRequestId] = sr; });

  // Filter tasks
  const filteredTasks = tasks.filter(t => {
    const s = (t.status || '').toUpperCase();
    if (filter === 'ALL')       return true;
    if (filter === 'PENDING')   return !['COMPLETED', 'CANCELLED'].includes(s);
    if (filter === 'COMPLETED') return s === 'COMPLETED';
    return true;
  });

  // Group by service request ID
  const grouped = {};
  filteredTasks.forEach(t => {
    const key = t.serviceRequestId || '__unassigned__';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(t);
  });

  const totalCount     = tasks.length;
  const pendingCount   = tasks.filter(t => !['COMPLETED', 'CANCELLED'].includes((t.status || '').toUpperCase())).length;
  const completedCount = tasks.filter(t => (t.status || '').toUpperCase() === 'COMPLETED').length;

  return (
    <DashboardLayout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">My Tasks</h1>
            <p className="text-sm text-gray-500 mt-1">
              Track and complete tasks assigned by your counselors
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="rounded-lg border-gray-200 text-gray-700 hover:border-[#1C8DEE] hover:text-[#1C8DEE]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm text-center">
            <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            <p className="text-sm text-gray-500 mt-0.5">Total Tasks</p>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm text-center">
            <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
            <p className="text-sm text-gray-500 mt-0.5">Pending</p>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-4 shadow-sm text-center">
            <p className="text-2xl font-bold text-emerald-600">{completedCount}</p>
            <p className="text-sm text-gray-500 mt-0.5">Completed</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                filter === f.key
                  ? 'bg-[#1C8DEE] text-white shadow-sm'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#1C8DEE] hover:text-[#1C8DEE]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Task Groups */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
                <div className="h-4 w-40 bg-gray-100 rounded mb-4" />
                <div className="space-y-3">
                  {[1, 2].map(j => (
                    <div key={j} className="h-12 bg-gray-50 rounded-lg" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : Object.keys(grouped).length > 0 ? (
          <div className="space-y-4">
            {Object.entries(grouped).map(([srId, srTasks]) => {
              const sr       = srLookup[srId];
              const srLabel  = sr
                ? SERVICE_TYPE_LABELS[sr.serviceType] || sr.serviceType
                : 'General Tasks';

              return (
                <Card key={srId} className="p-5 rounded-xl bg-white border border-gray-100 shadow-sm">
                  {/* Group header */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">{srLabel}</h3>
                    {sr && (
                      <button
                        className="text-xs text-[#1C8DEE] hover:underline flex items-center gap-1"
                        onClick={() => navigate(`/dashboard/service/${srId}`)}
                      >
                        View service <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  {/* Task rows */}
                  <div className="space-y-2">
                    {srTasks.map((task, i) => {
                      const statusKey = (task.status || 'PENDING').toUpperCase();
                      const sCfg      = STATUS_CONFIG[statusKey] || STATUS_CONFIG.PENDING;
                      const StatusIcon = sCfg.Icon;

                      return (
                        <div
                          key={task.taskId || i}
                          className="flex items-center gap-3 p-3 rounded-lg border border-gray-50 hover:bg-gray-50 transition-colors"
                        >
                          <StatusIcon className={`w-4 h-4 flex-shrink-0 ${
                            statusKey === 'COMPLETED'         ? 'text-emerald-500' :
                            statusKey === 'REVISION_REQUIRED' ? 'text-red-500'     :
                            statusKey === 'CANCELLED'         ? 'text-gray-400'    :
                                                                'text-amber-500'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {task.title || 'Untitled Task'}
                            </p>
                            <div className="flex items-center gap-3 mt-0.5">
                              {task.dueDate && (
                                <p className="text-xs text-gray-400">
                                  Due {format(new Date(task.dueDate), 'MMM d, yyyy')}
                                </p>
                              )}
                              {task.createdAt && !task.dueDate && (
                                <p className="text-xs text-gray-400">
                                  {formatDistanceToNow(new Date(task.createdAt), { addSuffix: true })}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${sCfg.className}`}>
                            {sCfg.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          /* Empty state */
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ListTodo className="w-7 h-7 text-gray-300" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">No tasks yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              Your counselor will assign tasks as your services progress
            </p>
            <Button
              className="mt-5 bg-[#1C8DEE] hover:bg-[#1a7fd4] text-white rounded-lg"
              onClick={() => navigate('/dashboard/track')}
            >
              View My Services
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
